import React, { useCallback, useEffect, useState } from 'react';
import { authClient, AuthError, Membership, SignedIn } from './authClient';
import { Shell, SignIn } from './PlatformAuth';
import { TokenSession } from './tokenSession';

// The console's front door: sign in, pick an account (skipped when there is
// only one), and hand a TokenSession to the console. The API-key screen stays
// reachable as a fallback.

interface Props {
  onReady: (session: TokenSession) => void;
  onUseApiKey: () => void;
}

const ConsoleSignIn: React.FC<Props> = ({ onReady, onUseApiKey }) => {
  const [signedIn, setSignedIn] = useState<SignedIn | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const open = useCallback(async (accountId: string) => {
    setError(null);
    setBusy(true);
    try {
      onReady(await TokenSession.start(accountId));
    } catch (err) {
      setError(err instanceof AuthError && err.status === 403 ? 'You no longer have access to that account.' : err instanceof Error ? err.message : 'Could not open the account.');
      setBusy(false);
    }
  }, [onReady]);

  const usable = (signedIn?.memberships ?? []).filter((m) => m.status === 'active');

  useEffect(() => {
    if (signedIn && usable.length === 1) {
      open(usable[0].accountId);
    }
    // Only when a new sign-in arrives.
  }, [signedIn]);

  const footer = (
    <div className="pa-links">
      <button type="button" className="btn btn-ghost" onClick={onUseApiKey}>Use an API key instead</button>
    </div>
  );

  if (!signedIn) {
    return <SignIn onSignedIn={setSignedIn} footer={footer} />;
  }

  const signOut = async () => {
    await authClient.logout().catch(() => undefined);
    setSignedIn(null);
  };

  if (usable.length === 0) {
    return (
      <Shell title="No accounts yet" intro="Your sign-in works, but you are not a member of any active account. Ask an administrator to add you.">
        <button className="btn btn-secondary setup-submit" type="button" onClick={signOut}>Sign out</button>
      </Shell>
    );
  }

  return (
    <Shell title={usable.length === 1 ? 'Opening your account…' : 'Choose an account'} intro={signedIn.person.email}>
      {error && (
        <div className="alert alert-error" role="alert">
          <span>Error</span>
          <span>{error}</span>
        </div>
      )}
      <ul className="pa-accounts">
        {usable.map((m: Membership) => (
          <li key={m.accountId}>
            <span>{m.name}</span>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => open(m.accountId)}>Open</button>
          </li>
        ))}
      </ul>
      <button className="btn btn-ghost setup-submit" type="button" onClick={signOut}>Sign out</button>
    </Shell>
  );
};

export default ConsoleSignIn;
